<script setup lang="ts">
import { ref, watch, onUnmounted, onMounted, nextTick } from 'vue';
import { Close } from '@vicons/ionicons5';
import MobileModal from '~/components/MobileModal.vue';
useHead({
  title: 'HopeAI - The AI-Native Workspace for Drug Development',
})

useSeoMeta({
  title: 'HopeAI - The AI-Native Workspace for Drug Development',
  description: 'HopeAI brings together specialized AI agents, proprietary data, and validated workflows to transform how new medicines are developed.',
  ogTitle: 'HopeAI - The AI-Native Workspace for Drug Development',
  ogDescription: 'HopeAI brings together specialized AI agents, proprietary data, and validated workflows to transform how new medicines are developed.',
  ogImage: 'https://hopeai.oss-us-east-1.aliyuncs.com/img/hopeai.png',
  ogUrl: 'https://hopeai.co',
  ogType: 'website',
  ogLocale: 'en_US',
  twitterTitle: 'HopeAI - The AI-Native Workspace for Drug Development',
  twitterDescription: 'HopeAI brings together specialized AI agents, proprietary data, and validated workflows to transform how new medicines are developed.',
  twitterImage: 'https://hopeai.oss-us-east-1.aliyuncs.com/img/hopeai.png',
  twitterCard: 'summary_large_image',
})

// HubSpot script is loaded via useHead above

const openModal = ref(false);
const formLoading = ref(false);
let scrollRevealObserver: IntersectionObserver | null = null;

const checkAndCreateForm = (silent = false) => {
    const frame = document.getElementById('hs-form-target');
    if (!frame) {
        if (!silent) console.error('❌ HubSpot target element #hs-form-target not found in DOM');
        formLoading.value = false;
        return;
    }

    if (frame.children.length > 0) {
        if (!silent) console.log('ℹ️ HubSpot form already exists in DOM');
        nextTick(() => {
            formLoading.value = false;
        });
        return;
    }

    const hbspt = (window as any).hbspt;
    if (hbspt && hbspt.forms) {
        if (!silent) console.log('✅ hbspt.forms found, calling create...');
        try {
            let formRenderTimeout: any = null;
            hbspt.forms.create({
                region: "na1",
                portalId: "23805798",
                formId: "2401e535-2cd1-4e4e-8a8c-98ace6a41db0",
                target: "#hs-form-target",
                onFormReady: () => {
                    if (!silent) console.log('🎉 HubSpot form ready in index!');
                    if (formRenderTimeout) clearTimeout(formRenderTimeout);
                    // Minimal delay to ensure iframe is painted
                    setTimeout(() => {
                        formLoading.value = false;
                    }, 100);
                },
                onFormSubmit: () => {
                    console.log('📩 HubSpot form submitted!');
                }
            });

            // Fallback timeout: if HubSpot doesn't fire onFormReady within 2s, hide spinner
            formRenderTimeout = setTimeout(() => {
                if (formLoading.value) {
                    if (!silent) console.warn('⚠️ HubSpot form ready callback timed out in index');
                    formLoading.value = false;
                }
            }, 2000);
        } catch (err) {
            console.error('❌ Error creating HubSpot form:', err);
            formLoading.value = false;
        }
    } else {
        if (!silent) console.error('❌ hbspt or hbspt.forms missing');
        formLoading.value = false;
    }
};

watch(openModal, (val) => {
    if (val) {
        formLoading.value = true;
        console.log('🔄 HubSpot modal opened, starting form creation...');
        
        nextTick(() => {
            const isHbsptReady = () => (window as any).hbspt && (window as any).hbspt.forms;

            if (isHbsptReady()) {
                checkAndCreateForm();
            } else {
                console.log('⏳ Waiting for HubSpot script to load...');
                const checkHbspt = setInterval(() => {
                    if (isHbsptReady()) {
                        clearInterval(checkHbspt);
                        console.log('✅ HubSpot script loaded dynamically!');
                        checkAndCreateForm();
                    }
                }, 200);

                setTimeout(() => {
                    if (checkHbspt) clearInterval(checkHbspt);
                    if (formLoading.value) {
                        console.warn('⚠️ HubSpot load timed out after 10s');
                        formLoading.value = false;
                    }
                }, 10000);
            }
        });
    }
});
const config = useRuntimeConfig()
const homeBanner = ref();
const isBottom = ref(false);
const isScrolledBeyondScreen = ref(false);

const imgUrl = (url: string) => {
    return config.app.cdnURL + url;
}

const isModal = ref(false);
const handleCHange = (val: any) => {
    isModal.value = val;
}
const close = () => {
    openModal.value = false;
}

const isMobile = ref(false);
const showMobileModal = ref(false);
const isReady = ref(false);
let resizeTimer: ReturnType<typeof setTimeout> | null = null;

const checkIsMobile = () => {
    // Check if mobile (screen width <= 768px)
    const newIsMobile = window.innerWidth <= 768;
    if (newIsMobile !== isMobile.value) {
        isMobile.value = newIsMobile;
    }
    // 设置页面准备就绪状态
    if (!isReady.value) {
        isReady.value = true;
    }
};

const debouncedCheckIsMobile = () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
        checkIsMobile();
    }, 250);
};

const openMobileModal = () => {
    showMobileModal.value = true;
};

const closeMobileModal = () => {
    showMobileModal.value = false;
};

const scrollBanner = () => {
    const homeWrapElement = homeBanner.value;
    if (!homeWrapElement) return;

    const scrollTop = homeWrapElement.scrollTop;
    const scrollHeight = homeWrapElement.scrollHeight;
    const clientHeight = homeWrapElement.clientHeight;

    isBottom.value = scrollTop + clientHeight >= scrollHeight - 50;
    isScrolledBeyondScreen.value = scrollTop > clientHeight;
};





let isSnapLocked = false;
let touchStartY = 0;

const handleHomeWheel = (e: WheelEvent) => {
    // 移动端不需要吸附逻辑
    if (window.innerWidth <= 768) return;
    const el = homeBanner.value;
    if (!el || isSnapLocked) return;

    const st = el.scrollTop;
    const vh = window.innerHeight;

    // 当用户在首屏（st 在 0 到 40px 内），向下滚轮时，自动吸附到第二屏
    if (st < 40 && e.deltaY > 10) {
        e.preventDefault();
        isSnapLocked = true;
        el.scrollTo({ top: vh, behavior: 'smooth' });
        setTimeout(() => { isSnapLocked = false; }, 650);
    } 
    // 当用户在第二屏顶部附近（st 在 vh - 40 到 vh + 40 内），向上滚轮时，自动吸附回首屏
    else if (st >= vh - 50 && st <= vh + 50 && e.deltaY < -10) {
        e.preventDefault();
        isSnapLocked = true;
        el.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => { isSnapLocked = false; }, 650);
    }
};

const handleTouchStart = (e: TouchEvent) => {
    // 移动端不需要触摸吸附逻辑
    if (window.innerWidth <= 768) return;
    touchStartY = e.touches[0].clientY;
};

const handleTouchEnd = (e: TouchEvent) => {
    // 移动端不需要触摸吸附逻辑
    if (window.innerWidth <= 768) return;
    const el = homeBanner.value;
    if (!el || isSnapLocked) return;

    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchStartY - touchEndY;
    const st = el.scrollTop;
    const vh = window.innerHeight;

    // 首屏上滑超过 40px，吸附到第二屏
    if (st < 40 && deltaY > 40) {
        isSnapLocked = true;
        el.scrollTo({ top: vh, behavior: 'smooth' });
        setTimeout(() => { isSnapLocked = false; }, 650);
    } 
    // 第二屏下滑超过 40px，吸附回首屏
    else if (st >= vh - 50 && st <= vh + 50 && deltaY < -40) {
        isSnapLocked = true;
        el.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => { isSnapLocked = false; }, 650);
    }
};

onMounted(() => {
    checkIsMobile();
    window.addEventListener('resize', debouncedCheckIsMobile);

    // 绑定滚轮与触摸吸附事件
    nextTick(() => {
        const container = homeBanner.value;
        if (container) {
            container.addEventListener('wheel', handleHomeWheel, { passive: false });
            container.addEventListener('touchstart', handleTouchStart, { passive: true });
            container.addEventListener('touchend', handleTouchEnd, { passive: true });
        }
    });

    // Scroll reveal logic
    nextTick(() => {
        const reveals = document.querySelectorAll('.reveal');
        const scrollContainer = homeBanner.value;
        
        scrollRevealObserver = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('visible');
                } else {
                    e.target.classList.remove('visible');
                }
            });
        }, { 
            threshold: 0.1, 
            root: scrollContainer, 
            rootMargin: '0px 0px -20px 0px' 
        });
        
        reveals.forEach(el => scrollRevealObserver!.observe(el));
    });

    // HubSpot script is now loaded globally in nuxt.config.ts
    // Pre-load HubSpot form in background
    nextTick(() => {
        const isHbsptReady = () => (window as any).hbspt && (window as any).hbspt.forms;
        if (isHbsptReady()) {
            checkAndCreateForm(true);
        } else {
            const checkHbspt = setInterval(() => {
                if (isHbsptReady()) {
                    clearInterval(checkHbspt);
                    checkAndCreateForm(true);
                }
            }, 1000);
            setTimeout(() => clearInterval(checkHbspt), 20000);
        }
    });
});

onUnmounted(() => {
    window.removeEventListener('resize', debouncedCheckIsMobile);
    if (resizeTimer) clearTimeout(resizeTimer);
    if (scrollRevealObserver) {
        scrollRevealObserver.disconnect();
        scrollRevealObserver = null;
    }
    const container = homeBanner.value;
    if (container) {
        container.removeEventListener('wheel', handleHomeWheel);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchend', handleTouchEnd);
    }
});
</script>

<template>
    <!-- 加载状态 -->
    <div v-if="!isReady" class="page-loading">
        <div class="loading-spinner"></div>
    </div>

    <!-- 页面内容 -->
    <div v-else class="home_wrap" ref="homeBanner"
        @scroll="scrollBanner">

        <headItem @change="handleCHange" :show-announcement="true" />
        <div class="animate_bg_wrap">
            <div class="animate_bg_box">
                <div class="animate01 animate_box animate_small">
                </div>
                <div class="animate02 animate_box animate_middle">
                </div>
                <div class="animate03 animate_box animate_large">
                </div>
                <div class="animate04 animate_box animate_middle">
                </div>
                <div class="animate05 animate_box animate_large">
                </div>
            </div>
        </div>
         
        <div class="banner_box">
            <div class="container">
                <div class="banner_container">
                    <div class="hero-content-wrapper">
                        <h1 class="hero-title anim-up">The AI-Native Workspace<br>for Drug Development</h1>
                        <p class="hero-desc anim-up anim-d1">HopeAI brings together specialized AI agents, proprietary data, and validated workflows to transform how new medicines are developed.</p>
                        <div class="anim-up anim-d2">
                            <a class="btn-gradient-float" style="margin-top: 0;" @click="openModal = true">Book a Demo</a>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <!-- ══════ WORKSPACE DEMO VIDEO ══════ -->
        <section class="workspace-demo-section" id="workspace-demo">
          <div class="container workspace-demo-container">
            <div class="workspace-demo-card">
              <iframe
                id="workspaceDemoIframe"
                class="workspace-demo-iframe"
                src="https://demo-workspace.hopeai.co?clean=1&embed=1"
                title="HopeAI Workspace Demo Video"
                allow="autoplay; fullscreen"
                allowfullscreen
                loading="lazy"
                frameborder="0"
              ></iframe>
            </div>
          </div>
        </section>

        <!-- 贯穿分割线：位于两板块真正物理交界线上 -->
        <div class="workspace-demo-divider"></div>

        <!-- ══════ VALUE ══════ -->
        <section class="value-section">
          <div class="container">
            <div class="reveal">
              <h2 class="section-title" style="color: #1a1035;">Better evidence <span class="gradient-text">leads to</span> better designs.<br>Better designs <span class="gradient-text" style="white-space: nowrap;">save trials</span>.<br></h2>
            </div>
            <div class="value-grid">
              <div class="value-card reveal">
                <div class="value-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-search-corner"><path d="M11.1 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.589 3.588A2.4 2.4 0 0 1 20 8v3.25"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/><path d="m21 22-2.88-2.88"/><circle cx="16" cy="17" r="3"/></svg>
                </div>
                <h3>Smarter Development Decisions</h3>
                <p>Make evidence-based decisions backed by expert curation and rigorous statistical methods.</p>
              </div>
              <div class="value-card reveal">
                <div class="value-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trending-down"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>
                </div>
                <h3>Leaner Study Designs</h3>
                <p>Reduce sample size and trial cost with adaptive designs and statistical innovations — without compromising power.</p>
              </div>
              <div class="value-card reveal">
                <div class="value-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <h3>Faster Trial Results</h3>
                <p>Accelerate time-to-readout through endpoint optimization.</p>
              </div>
              <div class="value-card reveal">
                <div class="value-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                </div>
                <h3>Higher Probability of Success</h3>
                <p>Increase trial success rate with evidence-backed assumptions and AI-powered statistical simulations.</p>
              </div>
            </div>
          </div>
        </section>

        <!-- ══════ METRICS ══════ -->
        <section class="metrics-section">
          <div class="container">
            <div class="reveal" style="margin-bottom:24px">
              <span class="section-label">Measurable Impact</span>
            </div>
            <div class="metrics-grid">
              <div class="metric-item reveal">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.0" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-timer"><line x1="10" x2="14" y1="2" y2="2"/><line x1="12" x2="15" y1="14" y2="11"/><circle cx="12" cy="14" r="8"/></svg>
                <div class="metric-details">
                  <div class="metric-value">1 <span class="metric-unit">Week</span></div>
                  <div class="metric-label">Systematic Literature Review</div>
                </div>
              </div>
              <div class="metric-divider"></div>
              <div class="metric-item reveal">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.0" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trending-down"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>
                <div class="metric-details">
                  <div class="metric-value">20<span class="metric-unit">%</span></div>
                  <div class="metric-label">Sample Size</div>
                </div>
              </div>
              <div class="metric-divider"></div>
              <div class="metric-item reveal">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.0" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trending-down"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>
                <div class="metric-details">
                  <div class="metric-value">2.5 <span class="metric-unit">Years</span></div>
                  <div class="metric-label">Time to Readout</div>
                </div>
              </div>
              <div class="metric-divider"></div>
              <div class="metric-item reveal">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.0" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trending-up"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                <div class="metric-details">
                  <div class="metric-value">30<span class="metric-unit">%</span></div>
                  <div class="metric-label">Success Probability</div>
                </div>
              </div>
            </div>
            <p class="metrics-note reveal">*Based on HopeAI's real-world collaborations with biopharma partners on evidence-based decisions and trial design optimization.</p>
          </div>
        </section>

        <!-- ══════ PRODUCTS ══════ -->
        <section class="products-section" id="products">
          <div class="container">
            <div class="reveal">
              <span class="section-label">Our Platform</span>
              <h2 class="section-title" style="margin-bottom: 12px;">How We Do It</h2>
              <p class="section-desc" style="color:#5a5278;max-width:920px;">HopeAI synthesizes comprehensive, up-to-date clinical evidence from the published literature and simulates a broad space of innovative design options, enabling clinical development teams to make faster, more confident decisions and design more efficient, ethical trials.</p>
            </div>
            <div class="products-list">
              <!-- Product 1 -->
              <div class="product-row reveal">
                <div class="product-left">
                  <div>
                    <h3 class="product-name">HopeAI <span class="gradient-text">Workspace</span></h3>
                    <span class="product-tag">AI-NATIVE WORKSPACE</span>
                  </div>
                  <div class="product-btn-box">
                    <a href="https://workspace.hopeai.co" target="_blank" class="btn-ghost" style="font-size:14px; padding: 12px 32px;">
                      Try HopeAI Workspace
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-left: 4px;"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
                    </a>
                  </div>
                </div>
                <div class="product-right">
                  <p class="product-desc">Your AI team for clinical development. HopeAI Workspace brings specialist AI agents into one environment, spanning Clinical, Biostatistics, Regulatory, Operations, and Commercialization. Ask in plain language and get reliable, evidence-grounded answers that move real work forward.</p>
                  <ul class="check-list">
                    <li>One Team of Specialist Agents</li>
                    <li>Built for Biopharma Workflows</li>
                    <li>Evidence-Grounded Rigor</li>
                  </ul>
                </div>
              </div>

              <!-- Product 2 -->
              <div class="product-row reveal">
                <div class="product-left">
                  <div>
                    <h3 class="product-name">PURE <span class="gradient-text">Evidence</span></h3>
                    <span class="product-tag">Evidence Intelligence</span>
                  </div>
                  <div class="product-btn-box">
                    <a href="/solutions/PUREEvidence" class="btn-ghost" style="font-size:14px; padding: 12px 32px;">Learn More →</a>
                  </div>
                </div>
                <div class="product-right">
                  <p class="product-desc">Synthesize literature, FDA reviews, conference abstracts, labels, and real-world data to pinpoint the right endpoints, comparators, benchmarks, and patient populations.</p>
                  <ul class="check-list">
                    <li>Live meta-analysis database</li>
                    <li>Subgroup &amp; endpoint extraction</li>
                    <li>Regulatory-grade structured data</li>
                  </ul>
                </div>
              </div>

              <!-- Product 3 -->
              <div class="product-row reveal">
                <div class="product-left">
                  <div>
                    <h3 class="product-name">Synth<span class="gradient-text">IPD</span></h3>
                    <span class="product-tag">Synthetic Data</span>
                  </div>
                  <div class="product-btn-box">
                    <a href="/solutions/synthIPD" class="btn-ghost" style="font-size:14px; padding: 12px 32px;">Learn More →</a>
                  </div>
                </div>
                <div class="product-right">
                  <p class="product-desc">Reconstruct patient-level data with SynthIPD to power deeper competitive insights, dynamic borrowing, and indirect treatment comparisons in oncology.</p>
                  <ul class="check-list">
                    <li>KM curve reconstruction</li>
                    <li>Privacy-preserving by design</li>
                    <li>Synthetic control arm creation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ══════ PUBLICATIONS ══════ -->
        <section id="publications" class="pubs-section">
          <div class="container">
            <div class="reveal">
              <span class="section-label">Scientific Rigor</span>
              <h2 class="section-title" style="margin-bottom:12px">Publications &amp; Research</h2>
              <p class="section-desc" style="color:#5a5278;max-width:920px;">Discover the latest breakthroughs in AI-powered clinical development from HopeAI's research team.</p>
            </div>
            <div class="pubs-grid">
              <a href="https://www.frontiersin.org/journals/artificial-intelligence/articles/10.3389/frai.2025.1683322/full" target="_blank" class="pub-card reveal">
                <div class="pub-card-img">
                  <img :src="imgUrl('/img/pub_cover_1.png')" alt="Neural network visualization">
                  <span class="pub-tag">Research paper</span>
                </div>
                <div class="pub-card-body">
                  <h4>PURE Evidence: AI for evidence-based treatment recommendation in oncology</h4>
                </div>
                <div class="pub-card-footer">
                  <div class="pub-card-meta">
                    <div class="pub-card-meta-row"><span class="pub-card-meta-label">Publisher</span><span>Frontiers in Artificial Intelligence</span></div>
                    <div class="pub-card-meta-row"><span class="pub-card-meta-label">Date</span><span>2025</span></div>
                  </div>
                  <div class="pub-card-arrow"><img :src="imgUrl('/img/right.svg')" alt=" " title=""></div>
                </div>
              </a>
              <a href="https://arxiv.org/html/2509.16466v1" target="_blank" class="pub-card reveal">
                <div class="pub-card-img">
                  <img :src="imgUrl('/img/pub_cover_2.png')" alt="Abstract wave visualization">
                  <span class="pub-tag">Research paper</span>
                </div>
                <div class="pub-card-body">
                  <h4>SynthIPD: Discover clinical insights and enhance statistical analyses with Synthetic individual patient data</h4>
                </div>
                <div class="pub-card-footer">
                  <div class="pub-card-meta">
                    <div class="pub-card-meta-row"><span class="pub-card-meta-label">Publisher</span><span>arXiv</span></div>
                    <div class="pub-card-meta-row"><span class="pub-card-meta-label">Date</span><span>2025</span></div>
                  </div>
                  <div class="pub-card-arrow"><img :src="imgUrl('/img/right.svg')" alt=" " title=""></div>
                </div>
              </a>
              <a href="https://ascopubs.org/doi/10.1200/JCO.2025.43.16_suppl.7547" target="_blank" class="pub-card reveal">
                <div class="pub-card-img">
                  <img :src="imgUrl('/img/pub_cover_3.png')" alt="Abstract curve visualization">
                  <span class="pub-tag">Research paper</span>
                </div>
                <div class="pub-card-body">
                  <h4>Leveraging AI for validating the association between minimal residual disease (MRD) and survival outcomes in multiple myeloma</h4>
                </div>
                <div class="pub-card-footer">
                  <div class="pub-card-meta">
                    <div class="pub-card-meta-row"><span class="pub-card-meta-label">Publisher</span><span>JCO</span></div>
                    <div class="pub-card-meta-row"><span class="pub-card-meta-label">Date</span><span>2025</span></div>
                  </div>
                  <div class="pub-card-arrow"><img :src="imgUrl('/img/right.svg')" alt=" " title=""></div>
                </div>
              </a>
            </div>
          </div>
        </section>

        <!-- ══════ CTA ══════ -->
        <section id="contact" class="cta-section">
  <div class="container">
    <div class="cta-box reveal">
      <div class="cta-bg"></div>
      <div class="cta-inner">
        <span class="section-label">Get Started</span>
        <h2>Ready to accelerate your<br>clinical program?</h2>
        <p>Talk to our team about how HopeAI can reduce your trial timeline by up to 2.5 years and cut costs by 20%.</p>
        <div style="display:flex;flex-direction:column;align-items:center;gap:16px">
          <a @click="openModal = true" class="btn-gradient-float">Book a Demo</a>
        </div>
      </div>
    </div>
  </div>
</section>

        <footItem />
        <!-- 关闭按钮：独立 fixed 覆盖层，完全在 n-modal/iframe DOM 树之外，确保移动端可点击 -->
        <div v-if="openModal" class="modal-close-overlay" @click="close">
            <n-icon size="20" style="color: #666;">
                <Close />
            </n-icon>
        </div>
        <n-modal v-model:show="openModal" :auto-focus="false" display-directive="show">
            <n-card style="width: 600px; max-width: 95vw; border-radius: 16px; overflow: hidden;" :bordered="false" size="huge" role="dialog" :aria-modal="true" :content-style="{ padding: 0 }">
                <!-- 桌面端关闭按钮，原始位置 -->
                <div class="modal-close-desktop" style="position: absolute; top: 20px; right: 20px; z-index: 100;">
                    <n-icon size="24" @click.prevent="close" style="cursor: pointer; color: #999;">
                        <Close />
                    </n-icon>
                </div>
                <div class="request_modal" id="hubspot-form-container">
                    <transition name="fade">
                        <div v-if="formLoading" class="form-loader-overlay">
                            <n-spin size="large" />
                        </div>
                    </transition>
                    <!-- HubSpot form will be injected here -->
                    <div class="hs-form-frame" id="hs-form-target" data-region="na1" data-form-id="2401e535-2cd1-4e4e-8a8c-98ace6a41db0" data-portal-id="23805798"></div>
                </div>
            </n-card>
        </n-modal>


        <MobileModal v-if="isMobile && showMobileModal" @close="closeMobileModal" />

        <!-- 移动端固定CTA按钮，滚动超过一屏时显示 -->
        <!-- <div v-if="isMobile && isScrolledBeyondScreen" class="mobile_fixed_cta_wrapper">
            <span class="mobile_fixed_cta_button" @click="openMobileModal">Get Early Access</span>
        </div> -->


    </div>
</template>

<style lang="scss">
.page-loading {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: #0D121F;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(122, 71, 185, 0.3);
    border-top: 3px solid #7A47B9;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}


.pb_player_box {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(13, 18, 31, 0.15);
    border-radius: 32px;
    transition: background 0.3s ease;

    .players {
        width: 100px !important;
        height: 100px;
        transition: transform 0.3s ease;
    }

    &:hover {
        background: rgba(13, 18, 31, 0.25);


    }
}

.video_box {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
}

.video-player {
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;

}

.hide {
    // display: none;
}

.ant-modal-mask {
    background: rgba(13, 18, 31, 0.85);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
}

.ant-modal-content {
    border-radius: 8px !important;
}

.ant-modal-body {
    padding: 48px !important;
}

@media screen and (max-width: 1024px) {
    .ant-modal-centered .ant-modal {
        width: 70% !important;
    }

    .video-player {
        width: 100%;
    }
}

@media screen and (max-width: 479px) {
    .ant-modal-centered .ant-modal {
        max-width: 100%;
        top: 0;
        padding-bottom: 0;
        margin: 0;
        width: 100% !important;
    }

    .ant-modal-content {
        display: flex;
        flex-direction: column;
        height: calc(100vh);
    }

    .ant-modal-body {
        flex: 1;
        padding: 32px !important;
        height: 100%;
        overflow: hidden;
    }

    .ant-modal-content {
        border-radius: 0 !important;
    }

    .profile_modal {
        padding-bottom: 60px;
    }
}

.bgWrap {
    position: relative;
}

.bgImg {
    position: absolute;
    top: 234px;
    width: 80%;
    right: -20px;
}

.dialogcon {
    position: relative;
    width: 100%;
    height: 100%;
}

.close {
    position: absolute;
    right: 20px;
    top: 20px;
    width: 30px;
    height: 30px;
    background: url(/img/close.svg) no-repeat center;
    background-size: 100%;
    z-index: 111;
    // cursor: pointer;
}

.dialog {
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    z-index: 100;
}

.video_box_01 {
    width: 100%;
    height: auto;
    position: relative;
    display: flex;
    justify-content: flex-end;
}

.video_box_right {
    width: 100%;
    height: auto;
    position: relative;
    display: flex;
    justify-content: flex-start;
}


.lb_ban {
    display: block;
    position: absolute;
    bottom: -88px;
    left: -70px;
    width: 300px;
    height: 300px;
    flex-shrink: 0;
    background: url(/img/Ellipse_lb.png) no-repeat center;
    filter: blur(50px);
}

.animate_bg_wrap {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    max-height: 100vh;
    height: 100%;
    overflow: hidden;
    z-index: -10;
    display: flex;
    align-items: center;
    justify-self: center;

    .animate_bg_box {
        position: absolute;
        width: 100%;
        z-index: -10;
        overflow: hidden;
    }

    .animate_box {
        filter: blur(64px);
    }

    .animate_small {
        width: 300px;
        height: 300px;
        border-radius: 50%;
        filter: blur(34px);
    }

    .animate_middle {
        width: 500px;
        height: 500px;
        border-radius: 50%;
        filter: blur(50px);
    }

    .animate_large {
        width: 600px;
        height: 600px;
        border-radius: 50%;
        filter: blur(74px);
    }

    .animate01 {
        background: #DC2DEE;
        opacity: 0.25;
        animation: 18s ease-in-out 0s infinite normal none running animateBlob2;
        transform: translate(80.8102vw, 7.86251vh);
    }

    .animate02 {
        background: #512685;
        opacity: 0.5;
        animation: 15s ease-in-out 0s infinite normal none running animateBlob1;
        transform: translate(81.4858vw, 83.6355vh);
    }

    .animate03 {
        background: #7A47B9;
        opacity: 0.5;
        animation: 18s ease-in-out 0s infinite normal none running animateBlob3;
        transform: translate(27.0167vw, 64.8606vh);
    }

    .animate04 {
        background: #BE35B0;
        opacity: 0.35;
        animation: 21s ease-in-out 0s infinite normal none running animateBlob4;
        transform: translate(20.5473vw, 35.7899vh);
    }

    .animate05 {
        background: #B588EC;
        opacity: 0.4;
        animation: 24s ease-in-out 0s infinite normal none running animateBlob5;
        transform: translate(66.0569vw, 50.9782vh);
    }
}

.play-bg {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 16px;
}

.vide-play-btn {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 70px;
    height: 70px;
    // cursor: pointer;
}







.banner_box {
    width: 100%;
    height: 100vh;
    // cursor: pointer;

    .speak_team {
        color: #fff;
        border-radius: 99px;
        border: 1px solid var(--brand-purple-5, #6836A8);
        background: var(--YI, #6939A3);
        display: inline-block;
        padding: 12px 14px;
        font-size: 16px;
        font-weight: 500;
        margin-top: 90px;
        z-index: 2;
        padding: .7rem 1.5rem;
        font-weight: 500;
        position: relative;
        z-index: 100;
        cursor: pointer;

        &:hover {
            background: #7A47B9FF;
        }
    }

    .container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        position: relative;
        padding-top: 90px;
        // cursor: pointer;
    }

    .banner_container {
        // max-width: 910px;
        margin: 0;
        width: 96%;
        // cursor: pointer;
    }



}



.banner_box .banner_img {
    width: 32px;
    height: 32px;
    display: block;
    margin: 0px auto 40px auto;
    animation: bounce 2s infinite;
    cursor: pointer;
}

@keyframes bounce {

    0%,
    20%,
    50%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-10px);
    }

    60% {
        transform: translateY(-5px);
    }
}



.partner_box {
    padding: 0 0 120px 0;

    .container {
        display: flex;
        justify-content: center;
        flex-direction: column;
    }
}

.partner_banner_title {
    color: #fff;
    text-align: center;
    font-size: 24px;
    font-weight: 500;
    line-height: 150%;
    z-index: 10;
    margin-bottom: 20px;
}

.partner_wrap {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 800px;
    margin: 0 auto;
    gap: 54px;

    img {
        height: 136px;
        width: auto;
    }
}

.unlock_wrap {
    background: linear-gradient(133deg, #673E9A 6.2%, #52327B 89.06%);
    padding: 120px 0;


    .container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
    }

    .clinician_box {
        display: flex;
        flex-direction: column;
        gap: 60px;

        .title {
            font-size: 36px;
            text-align: center;
            width: calc(100% - 300px);
            margin: 0 auto;
            font-weight: 600;
            color: #fff;
            line-height: 44px;

        }

        .img_box {
            img {
                width: 100%;
                border-radius: 32px;
            }
        }
    }

    .work_item_box {
        margin-top: 60px;
        display: flex;
        flex-direction: column;
        gap: 60px;

        .work_item {
            display: flex;
            gap: 60px;
            padding: 36px 40px;
            border-radius: 32px;
            border: 1px solid rgba(0, 0, 0, 0.04);
            background: rgba(0, 0, 0, 0.2);

            .work_item_left,
            .work_item_right {
                flex: 1;
            }

            .work_item_right {
                img {
                    width: 100%;
                    border-radius: 8px;
                }
            }
        }
    }

    .product_partner {
        display: flex;
        flex-direction: column;
        gap: 30px;
        color: #fff;

        h2 {
            font-size: 36px;
            font-weight: 600;
            line-height: 44px;
        }

        p {
            font-size: 24px;
            line-height: 38px;
        }

        .partner_img_box {
            display: flex;
            gap: 30px;

            img {
                height: 64px;
            }
        }

        .btn {
            position: relative;
            z-index: 100;
            cursor: pointer;
            border-radius: 99px;
            border: 1px solid #6836A8;
            background: #6939A3;
            font-size: 16px;
            font-weight: 600;
            width: 168px;

            a {
                color: #fff;
                font-weight: 600;
                display: block;
                width: 100%;
                height: 100%;
                padding: 7px 24px;
            }
        }
    }

    .statistician {
        display: flex;
        align-items: center;

        h2 {
            font-size: 36px;
            color: #fff;
            line-height: 56px;
            font-weight: 600;
        }
    }




}

.solution_box {
    background: #F8F0FF;
    padding: 60px 0;
}

.solution_product1,
.solution_product2 {
    // padding: 120px;

    .flex {
        padding: 60px 100px;
        gap: 100px;
        border-bottom: 1px solid #999;
        align-items: center;
        // &:last-child {
        //     border-bottom: none;
        // }
    }

    .solution_title {
        font-size: 72px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }

    ul {

        li {
            font-size: 24px;
            font-style: normal;
            line-height: 32px;
            margin-bottom: 8px;
        }
    }
}

.solution_product {
    &:last-child {
        .flex {
            border-bottom: none;
        }
    }
}

.solution_shade {
    background: linear-gradient(98deg, #802DF5 22.91%, #DC2DEE 58.87%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 72px;
    font-weight: 700;
    font-style: normal;
    text-shadow: none;
    line-height: normal;
}

.last_product {
    .container {
        border-bottom: none !important;
    }
}

.product_box {
    .container {
        padding: 120px 0;
        border-bottom: 1px solid rgba(193, 193, 202, 0.40);

    }

    .flex {
        gap: 48px;
    }

    .product_title {
        color: #FFF;
        font-size: 40px;
        font-style: normal;
        font-weight: 700;
        line-height: 52px;
    }

    .shade {
        font-size: 40px;
        font-style: normal;
        font-weight: 700;
        line-height: 52px;
    }

    .pro_title {
        color: #FFF;
        font-size: 40px;
        font-style: normal;
        font-weight: 700;
        line-height: 52px;
    }

    .pro_content {
        color: rgba(255, 255, 255, 0.80);
        font-size: 20px;
        font-style: normal;
        font-weight: 300;
        line-height: 24px;

        i {
            font-style: normal;
            color: #fff;
            font-weight: 500;
        }
    }

    .pro_left {
        flex: 4;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 48px;


    }

    .pro_right {
        flex: 6;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 48px;

        p {
            color: #FFF;
            font-size: 24px;
            font-style: normal;
            font-weight: 400;
            line-height: 32px;

            &.btn {
                position: relative;
                z-index: 100;
                cursor: pointer;
                border-radius: 99px;
                border: 1px solid #6836A8;
                background: #6939A3;
                font-size: 16px;
                font-weight: 600;

                a {
                    color: #fff;
                    font-weight: 600;
                    display: block;
                    width: 100%;
                    height: 100%;
                    padding: 7px 24px;
                }

                &:hover {
                    background: #7A47B9FF;
                }
            }
        }
    }
}

.banner_box {
    .banner_top {
        position: relative;
        // width: 527px;
        margin: 0 auto;
        color: #FFF;
        text-align: left;
        line-height: 150%;
        z-index: 10;
        text-shadow: 2px 2px 0px rgba(0, 0, 0, 0.25);
        // margin-top: 30px;

        .new_slogan {
            margin-bottom: 30px;
            font-weight: 500;
            font-size: 38px;
        }

        span {
            display: block;
            position: relative;
            z-index: 10;
            color: #FFF;
            text-shadow: 2px 2px 0px rgba(0, 0, 0, 0.25);
            font-size: 36px;
            font-style: normal;
            font-weight: 300;
            line-height: normal;
        }

        .slogan {
            font-size: 72px;
            font-weight: 700;
            margin-bottom: 32px;

            i {
                font-style: normal;
                font-weight: 700;
            }

        }

        .shade {
            font-weight: 800;
            font-size: 72px;
        }


    }
}


.shade {
    background: linear-gradient(98deg, #802DF5 22.91%, #DC2DEE 58.87%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 56px;
    font-style: normal;
    font-weight: 800;
    text-shadow: none;
    line-height: 110%;
}

.nav_product1,
.nav_product2 {
    padding: 80px 0;

    .flex {
        gap: 60px;
    }
}

.nav_product4 {
    padding: 80px 0 120px 0;
}

.product_title {
    font-family:
        -apple-system, BlinkMacSystemFont, segoe ui, Roboto,
        Helvetica Neue, Helvetica, Arial, sans-serif;
    font-style: normal;
    font-weight: 800;
    font-size: 56px;
    line-height: 62px;
    color: #FFFFFF;
    order: 2;
    align-self: stretch;
    flex-grow: 0;
    position: relative;
    z-index: 10;
}

.flex {
    display: flex;
}

.flex_left {
    flex: 4;
}

.flex_right {
    flex: 6;
    display: flex;
}

.product01_img {
    justify-content: end;
    align-items: baseline;
}

.product02_img {
    justify-content: start;
    align-items: baseline;
}


.product_content {
    margin-top: 32px;
    font-family:
        -apple-system, BlinkMacSystemFont, segoe ui, Roboto,
        Helvetica Neue, Helvetica, Arial, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 20px;
    line-height: 36px;
    color: #C1C1CA;
    flex: none;
    order: 1;
    flex-grow: 0;
    // display: none;
}

.product_img01 {
    width: 100%;
    display: block;
    border-radius: 16px;
}

.product_img02 {
    width: 100%;
    display: block;
    border-radius: 16px;
}



@keyframes animateBlob1 {
    0% {
        transform: translate(11.82079309187229vw, 24.96595982386701vh);
    }

    25% {
        transform: translate(24.54175811702291vw, 35.70676736748425vh);
    }

    50% {
        transform: translate(38.161019089972356vw, 81.32325729877667vh);
    }

    75% {
        transform: translate(89.61830534629931vw, 7.836224200551767vh);
    }

    100% {
        transform: translate(81.82079309187229vw, 64.96595982386701vh);
    }
}

@keyframes animateBlob2 {
    0% {
        transform: translate(69.7130711321029vw, 37.29391065427885vh);
    }

    25% {
        transform: translate(10.233413765149656vw, 101.13517608566744vh);
    }

    50% {
        transform: translate(27.408715586013233vw, 63.66088453729726vh);
    }

    75% {
        transform: translate(32.69164224091578vw, 91.68110024118046vh);
    }

    100% {
        transform: translate(69.7130711321029vw, 37.29391065427885vh);
    }
}

@keyframes animateBlob3 {
    0% {
        transform: translate(64.83222381153115vw, 56.9305523739702vh);
    }

    25% {
        transform: translate(45.59169118011894vw, 55.114131053974404vh);
    }

    50% {
        transform: translate(36.23736674882843vw, 96.22568076101967vh);
    }

    75% {
        transform: translate(71.78715604036958vw, 32.70317400608938vh);
    }

    100% {
        transform: translate(64.83222381153115vw, 56.9305523739702vh);
    }
}

@keyframes animateBlob4 {
    0% {
        transform: translate(52.3226425772783vw, 80.39994368317865vh);
    }

    25% {
        transform: translate(82.82976877739536vw, 16.9646167524403vh);
    }

    50% {
        transform: translate(83.49595269413092vw, 86.536671377477vh);
    }

    75% {
        transform: translate(90.04273334087989vw, 82.64523635602355vh);
    }

    100% {
        transform: translate(52.3226425772783vw, 80.39994368317865vh);
    }
}

@keyframes animateBlob5 {
    0% {
        transform: translate(42.240409236308096vw, 86.07545364265717vh);
    }

    25% {
        transform: translate(92.57433845288628vw, 88.86549267210148vh);
    }

    50% {
        transform: translate(3.046095758175893vw, 90.33367652224335vh);
    }

    75% {
        transform: translate(11.213993850126913vw, 67.10271016858415vh);
    }

    100% {
        transform: translate(42.240409236308096vw, 86.07545364265717vh);
    }
}








/* Mobile banner specific styles */
.mobile_banner_box {
    background: url(/img/mobile_wel_bg.png) no-repeat center;
    background-size: cover;
}

.mobile_banner_container {
    padding: 20px;
    padding-top: calc(20px + env(safe-area-inset-top));
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    text-align: center;
    backdrop-filter: blur(5px);
    background: rgba(13, 18, 31, 0.5);
}

.mobile_banner_content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-width: 100%;
    margin: 0;
    text-align: left;
}

.mobile_arrow_box {
    padding-bottom: calc(70px + env(safe-area-inset-bottom));
    margin-top: 20px;
}

.mobile_launch_tag {
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 36px;
    background: linear-gradient(70deg, #802DF5 16.91%, #DC2DEE 72.87%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 16px;
    /* 28.8px */
}

.mobile_title {
    color: #fff;
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 36px;

}

.mobile_subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 20px;

}

.mobile_description {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 60px;
}

.mobile_cta_button {
    display: inline-block;
    background: var(--Linear, linear-gradient(98deg, #BE35B0 -6.37%, #7A47B9 87.51%));
    box-shadow: 0.903px 2.71px 3.614px 0 rgba(0, 0, 0, 0.25);
    color: #fff;
    padding: 0px 24px;
    border-radius: 999px;
    font-size: 20px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    align-self: flex-start;
    line-height: 48px;
    height: 48px;
}

.mobile_cta_button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(128, 45, 245, 0.4);
}

.mobile_cta_button_fixed {
    position: fixed;
    top: 60px;
    left: 20px;
    z-index: 1000;
    animation: slideDownFade 0.3s ease-out;
}

@keyframes slideDownFade {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 移动端固定CTA按钮样式 */
.mobile_fixed_cta_wrapper {
    position: fixed;
    top: 110px;
    left: 20px;
    z-index: 999;
    animation: slideDownFade 0.3s ease-out;
}

.mobile_fixed_cta_button {
    display: inline-block;
    background: var(--Linear, linear-gradient(98deg, #BE35B0 -6.37%, #7A47B9 87.51%));
    box-shadow: 0.903px 2.71px 3.614px 0 rgba(0, 0, 0, 0.25);
    color: #fff;
    padding: 10px 24px;
    border-radius: 999px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.mobile_fixed_cta_button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(128, 45, 245, 0.4);
}

.mobile_arrow_box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-top: 40px;
    cursor: pointer;
}

.mobile_arrow_text {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.5px;
    animation: fadeInUp 1.5s ease-out;
}

.mobile_arrow {
    width: 24px;
    height: 24px;
    animation: mobileBounce 2s infinite;
    cursor: pointer;
    filter: brightness(0) invert(1);
}

@keyframes mobileBounce {

    0%,
    20%,
    50%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-8px);
    }

    60% {
        transform: translateY(-4px);
    }
}

/* Mobile Modal Styles */
.mobile_modal_overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(8px);
    z-index: 3000;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: env(safe-area-inset-bottom);
    animation: fadeIn 0.3s ease-out;
}

.mobile_modal_content {
    background: rgba(255, 255, 255, 0.99);
    border-radius: 24px 24px 0 0;
    width: 100%;
    max-width: 600px;
    height: 86vh;
    overflow: hidden;
    animation: slideUp 0.3s ease-out;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
}

.mobile_modal_header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

}

.mobile_modal_close {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: rgb(5, 3, 3);
}

.mobile_modal_close:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
}

.mobile_modal_body {
    padding: 0;
    min-height: 200px;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes fadeOut {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
    }
}

@keyframes slideUp {
    from {
        transform: translateY(100%);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes slideDown {
    from {
        transform: translateY(0);
        opacity: 1;
    }

    to {
        transform: translateY(100%);
        opacity: 0;
    }
}

@media (max-width: 576px) {
    .partner_banner_title {
        font-size: 20px;
    }

    .video_box {
        .video-player {
            border-radius: 16px;
        }
    }

    .pb_player_box {
        border-radius: 16px;
    }

    .video_box img {
        border-radius: 16px !important;
    }

    .partner_box {
        margin-top: 60px;
    }

    .partner_wrap {
        max-width: 100%;
        gap: 20px;

        img {
            height: 68px;
        }
    }

    .banner_box {
        .container {
            padding-top: 0;
        }

        .banner_title {
            font-size: 20px;
            margin: 81px auto 20px auto;
        }

        .banner_container {
            max-width: 100%;
            margin: 0 auto;
        }

        .banner_top {
            font-size: 40px;

            .new_slogan {
                font-size: 22px;
                line-height: 30px;
                width: calc(100% - 46px);
            }

            .slogan {
                font-size: 34px;
                margin-bottom: 24px;
                display: block;

                i {
                    display: block;
                }
            }

            span {
                font-size: 20px;
                display: inline;
            }

            .shade {
                font-size: 40px;
            }

            p {
                line-height: 14px;
            }
        }

        .partner_wrap {
            width: 100%;
            justify-content: flex-start;

            img {
                height: 75px;
            }

        }
    }


    .lb_ban {
        width: 200px;
        height: 200px;
        left: -30px;
        bottom: -72px;
    }

    .nav_product1 {
        padding: 60px 0;

        .flex {
            flex-flow: column-reverse;
        }
    }

    .product_title {
        font-size: 32px;
        line-height: 36px;

        .shade {
            font-size: 32px;
            line-height: 36px;
        }
    }

    .product01_img,
    .product02_img {
        margin-bottom: 26px;
    }

    .product_img01,
    .product_img02 {
        width: 100%;
    }

    .nav_product2 {
        padding: 60px 0;

        .flex {
            flex-flow: column;
        }
    }

    .nav_box {
        padding-bottom: 80px;
    }


    .unlock_wrap {
        padding: 60px 0;
        height: auto;



    }

    .solution_shade {
        font-size: 40px;
    }

    .solution_product1,
    .solution_product2 {
        .flex {
            align-items: flex-start;
            gap: 40px;
            padding: 60px 0;
        }

        .solution_title {
            font-size: 40px;
        }

        ul {
            padding-left: 20px;

            li {

                font-size: 20px;
            }
        }
    }

    .solution_product1 {
        .flex {
            flex-direction: column;
        }
    }

    .solution_product2 {
        .flex {
            flex-direction: column-reverse;
        }
    }

    .product_box {
        .flex {
            flex-direction: column;
        }

        .pro_title {
            font-size: 30px;
            line-height: 40px;
        }

        .shade {
            font-size: 30px;
            line-height: 40px;
        }

        .pro_right {
            p {
                font-size: 20px;
            }
        }
    }

    .unlock_wrap {
        .clinician_box {

            gap: 40px;

            .title {
                width: 100%;
                font-size: 24px;
                line-height: 30px;
            }
        }

        .work_item_box {
            .work_item {
                flex-direction: column;
                gap: 20px;
                padding: 14px 16px;
                border-radius: 16px;
            }
        }

        .product_partner {
            gap: 20px;

            h2 {
                font-size: 24px;
                line-height: 30px;
            }

            p {
                font-size: 16px;
                line-height: 26px;
            }
        }

        .statistician {
            h2 {
                font-size: 24px;
                line-height: 30px;
            }
        }
    }
}

@media (max-width:390px) {
    .banner_box .banner_top .slogan {
        font-size: 32px;
    }

    .banner_box {
        .container {
            margin: 0 0 0 20px;
        }
    }
}

@media (width:390px) {
    .banner_box .banner_top .slogan {
        font-size: 34px;
    }
}

@media (min-width: 391px) and (max-width: 577px) {
    .banner_box .banner_top .slogan {
        font-size: 34px;
    }
}

@media (min-width: 577px) and (max-width: 768px) {
    .nav_product1 {
        padding: 60px 0;

        .flex {
            flex-flow: column-reverse;
        }
    }

    .product_title {
        font-size: 32px;
        line-height: 36px;

        .shade {
            font-size: 32px;
            line-height: 36px;
        }
    }

    .product01_img,
    .product02_img {
        margin-bottom: 26px;
    }

    .product_img01,
    .product_img02 {
        width: 100%;
    }

    .nav_product2 {
        padding: 60px 0;

        .flex {
            flex-flow: column;
        }
    }



    .solution_shade {
        font-size: 56px;
    }

    .solution_product1,
    .solution_product2 {
        .flex {
            align-items: flex-start;
            gap: 40px;
            padding: 60px 0;
        }

        .solution_title {
            font-size: 56px;
        }

        ul {
            padding-left: 20px;


        }
    }

    .solution_box {
        padding: 80px 0;
    }

    .solution_product1 {
        .flex {
            flex-direction: column;
        }
    }

    .solution_product2 {
        .flex {
            flex-direction: column-reverse;
        }
    }

    .product_box {
        .flex {
            flex-direction: column;
        }

        .pro_title {
            font-size: 30px;
            line-height: 40px;
        }

        .shade {
            font-size: 30px;
            line-height: 40px;
        }

        .pro_right {
            p {
                font-size: 20px;
            }
        }
    }
}

@media (min-width: 769px) and (max-width: 1365px) {
    .partner_box {
        margin-top: 120px;
    }

    .banner_box {
        .banner_top {
            .slogan {
                font-size: 50px;
            }

            span {
                font-size: 28px;
            }

            .new_slogan {
                font-size: 28px;
            }
        }
    }

    .nav_product1 .flex,
    .nav_product2 .flex {
        gap: 44px;
    }


    .footer {
        padding: 14px 0;
    }


    .lb_ban {
        left: 0;
        filter: blur(76px);
        bottom: 22%;
    }

    .foot_wrap {
        flex-wrap: wrap;
        height: auto;
        margin-top: 20px;
    }



    .solution_box {
        padding: 80px 0;
    }

    .solution_product1,
    .solution_product2 {
        .flex {
            padding: 30px 0;
        }

        ul {
            li {
                font-size: 22px;
            }
        }

        .solution_title {
            font-size: 60px;
        }

    }

    .solution_shade {
        font-size: 60px;
    }

    .product_box {
        .pro_title {
            font-size: 30px;
            line-height: 40px;
        }

        .shade {
            font-size: 30px;
            line-height: 40px;
        }

        .pro_right {
            p {
                font-size: 20px;
            }
        }
    }

    .unlock_wrap {
        .product_partner {
            h2 {
                font-size: 28px;
            }

            p {
                font-size: 18px;
                line-height: 32px;
            }
        }

        .statistician {
            h2 {
                font-size: 28px;
                line-height: 46px;
            }
        }

    }



}

.hero-content-wrapper {
  position: relative;
  z-index: 10;
  text-align: left;
  width: 100%;
}

.hero-title {
  font-size: 72px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 24px;
  color: #fff;
  text-shadow: 2px 2px 0px rgba(0, 0, 0, 0.25);
}
.hero-title .gradient-text {
  font-weight: 700;
  background: linear-gradient(135deg, #6836a8 0%, #be35b0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: none;
}
.hero-desc {
  font-size: 20px;
  font-weight: 300;
  color: #fff;
  max-width: 600px;
  line-height: 1.5;
  margin-bottom: 40px;
  text-shadow: 2px 2px 0px rgba(0, 0, 0, 0.25);
}

@media (max-width: 768px) {
  .hero-title { font-size: 42px; }
  .hero-desc { font-size: 18px; }
}


</style>
<style>
/* Custom Sections Styles */
.gradient-text {
  font-weight: 800;
  background: linear-gradient(135deg, #6836a8 0%, #be35b0 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline;
}
.section-label{
  display:inline-block;font-size:12px;font-weight:700;
  text-transform:uppercase;letter-spacing:2px;
  color:#b794e0;
  background:rgba(104,54,168,0.08);
  -webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);
  border:1px solid rgba(140,100,210,0.18);border-radius:9999px;
  padding:4px 16px;margin-bottom:16px;
}
.section-title{
  font-size:48px;font-weight:800;
  letter-spacing:-1px;line-height:1.2;color:#fff;
  margin-bottom:16px;
}

/* Scroll reveal animations */
.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity .7s ease, transform .7s ease;
}
.reveal.visible {
  opacity: 1;
  transform: translateY(0);
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(32px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.anim-up {
  animation: fadeUp .7s ease-out both;
}
.anim-d1 {
  animation-delay: .1s;
}
.anim-d2 {
  animation-delay: .2s;
}
.anim-d3 {
  animation-delay: .3s;
}

.value-section{
  background:#F8F0FF;padding:96px 0;
  position:relative;
}
.value-grid{
  display:grid;grid-template-columns:repeat(auto-fit,minmax(252px,1fr));
  gap:24px;margin-top:48px;
}
.value-card{
  border:1px solid rgba(255,255,255,0.8);border-radius:32px;padding:32px;
  background:rgba(255,255,255,0.4);
  -webkit-backdrop-filter:blur(20px);backdrop-filter:blur(20px);
  box-shadow:0 8px 32px rgba(104,54,168,0.08);
  position:relative;
}
.value-icon{
  width:48px;height:48px;border-radius:12px;
  background:rgba(104,54,168,0.08);
  display:flex;align-items:center;justify-content:center;
  margin-bottom:20px;color:#6836a8;
}
.value-card h3{font-size:20px;font-weight:700;margin-bottom:12px;color:#1a1035;}
.value-card p{font-size:16px;color:#5a5278;line-height:1.6;}

.metrics-section{
  background:linear-gradient(135deg, #420f83 0%, #820084 100%);
  padding:64px 0;position:relative;overflow:hidden;
}
.metrics-section .section-label {
  color: #fff;
  background: rgba(255,255,255,0.15);
  border-color: rgba(255,255,255,0.25);
}
.metrics-grid{
  display:grid;grid-template-columns: 1fr auto 1fr auto 1fr auto 1fr;
  align-items:flex-start;
  margin-bottom:16px;position:relative;
  width: 100%;
}
.metric-divider {
  width:1px;height:64px;margin: 16px 32px 0 32px;
  background:linear-gradient(180deg,transparent,rgba(255,255,255,0.2),transparent);
  flex-shrink: 0;
}
.metric-item{text-align:left;position:relative;padding:16px 0;display:flex;gap:16px;align-items:flex-start;width:auto}
.metric-details{display:flex;flex-direction:column; min-width: 0;}
.metric-value{
  font-size:48px;font-weight:700;
  line-height:1;margin-bottom:8px;
  color:#fff;
  letter-spacing:-0.03em;
  white-space:nowrap;
}
.metric-unit {
  font-weight: 700;
}
.metric-item svg{
  width:40px;height:40px;opacity:0.9;
  stroke: #BE35B0;
  flex-shrink:0;
  margin-top:4px;
}
.metric-label{font-size:18px;font-weight:500;margin-bottom:8px;color:#fff;letter-spacing:-0.02em; overflow-wrap: break-word;}
.section-desc{font-size:20px;line-height:1.6;margin-bottom:48px}
.section-quote{font-size:20px;color:#aaa5ba;font-style:italic;line-height:1.5}

@media(max-width:1200px){
  .metric-divider{margin: 16px 20px 0 20px}
  .metric-value{font-size:40px}
}
@media(max-width:1024px){
  .metrics-grid{grid-template-columns: repeat(2, 1fr); gap: 48px 32px;}
  .metric-divider{display:none}
  .metric-value{font-size:36px}
  .metric-item{padding: 0}
}
@media(max-width:768px){
  .metrics-grid{gap:32px 20px}
  .metric-item{gap: 12px;}
  .metric-value{font-size:32px}
  .metric-unit{font-size:20px}
  .metric-label{font-size:14px !important}
  .section-title{font-size:32px}
  .section-desc{font-size:16px; margin-bottom:32px}
  .section-quote{font-size:16px}
}
@media(max-width:480px){
  .metrics-grid{gap:24px 12px}
  .metric-value{font-size:32px}
  .metric-item svg { width: 28px; height: 28px; flex-shrink:0 }
}

.metric-sub{font-size:20px;color:rgba(255,255,255,0.85);line-height:1.6}
.metrics-note{font-size:14px;color:rgba(255,255,255,0.7);text-align:left;position:relative}

.products-section{
  background:#F8F0FF;
  padding:96px 0;
  position:relative;
}
.products-section .section-title { color: #1a1035; }
.products-section .section-label { color:#6836a8; background:rgba(104,54,168,0.08); border-color:rgba(104,54,168,0.18) }

.products-list{
  display:flex;flex-direction:column;gap:32px;
  margin-top: 48px;
}
.product-row{
  display:grid;grid-template-columns: 420px 1fr;
  gap: 64px;
  border:1px solid rgba(255,255,255,0.8);
  border-radius:32px;
  padding:48px;
  background:rgba(255,255,255,0.4);
  -webkit-backdrop-filter:blur(20px);backdrop-filter:blur(20px);
  box-shadow:0 8px 32px rgba(104,54,168,0.08);
  position:relative;
  overflow:visible;
  margin: 0;
}
.product-left{
  position: relative;
  background:transparent;
  padding: 0;
  display:flex;flex-direction:column;justify-content:space-between;gap:40px;
}
.product-name{font-size:48px;font-weight:800;color:#1a1035;line-height:1.2}
.product-right{
  padding: 0;
  display:flex;flex-direction:column;gap:32px;justify-content:flex-start;
}
.product-desc{font-size:20px;color:#1A1035;line-height:1.6}
.product-tag{
  display:block;font-size:12px;font-weight:700;
  color:#b794e0;text-transform:uppercase;
  margin-top:12px;width:fit-content;
}
.check-list{display:flex;flex-direction:column}
.check-list li{
  display:flex;align-items:center;gap:12px;
  font-size:20px;color:#1A1035;
  padding: 4px 0;
}
.check-list li::before {
  content: "";
  width: 6px;
  height: 6px;
  background: #6836a8;
  border-radius: 50%;
  flex-shrink: 0;
}
.check-list li:last-child{border-bottom:none}
.btn-ghost{
  color:#fff;border:1px solid #6836a8;
  background:#6836a8;border-radius:9999px;
  -webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);
  padding:8px 24px;font-size:12px;font-weight:600;
  cursor:pointer;
  display:inline-flex;align-items:center;gap:4px;
  transition:all .25s;
  white-space:nowrap;
  text-decoration: none;
}
.btn-ghost:hover{border-color:#7c4fb6;color:#fff !important;background:#7c4fb6;transform:translateY(-2px);box-shadow:0 6px 16px rgba(124,79,182,0.3);}
.product-btn-box{margin-top:auto;display:flex;align-items:flex-end;}

@media(max-width:920px){
  .product-row{
    display:flex;
    flex-direction:column;
    gap:32px;
    padding:32px 24px;
    margin:0;
    border:1px solid rgba(255,255,255,0.8);
  }
  .product-left, .product-right{
    display: contents !important;
  }
  .product-left > div:first-child { order: 1; }
  .product-desc { order: 2; }
  .check-list { order: 3; }
  .product-btn-box { order: 4; margin-top: 8px; }
  
  .product-left, .product-right{padding:0 !important}
  .product-left{border-bottom:none}
  .product-left:after { display: none; }
  .product-name{font-size:32px}
  .product-desc{font-size:16px}
  .check-list li{font-size:16px}
}

.value-section .section-title { color: #1a1035; }
.pubs-section .section-label { color:#6836a8; background:rgba(104,54,168,0.08); border-color:rgba(104,54,168,0.18) }
.pubs-section .section-title { color: #1a1035; }
.pubs-section{
  background:#F8F0FF;
  padding:96px 0;
  border-top: 1px solid rgba(104, 54, 168, 0.2);
  position:relative;overflow:hidden;
}
.pubs-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:24px}
.pub-card{
  display:flex;flex-direction:column;
  border:none;border-radius:20px;
  background:#fff;
  box-shadow:0 4px 16px rgba(104,54,168,0.06);
  overflow:hidden;cursor:pointer;position:relative;
  transition:transform .4s,box-shadow .4s;
  padding:0;
  text-decoration: none;
}
.pub-card:hover{
  transform:translateY(-8px);
  box-shadow:0 20px 48px rgba(104,54,168,0.14),0 0 0 1px rgba(104,54,168,0.15);
}
.pub-card-img{
  position:relative;height:200px;overflow:hidden;
}
.pub-card-img img{
  width:100%;height:100%;object-fit:cover;
  transition:transform .5s;
}
.pub-card:hover .pub-card-img img{transform:scale(1.06)}
.pub-card-img .pub-tag{
  position:absolute;bottom:16px;left:16px;
  color:#fff;background:rgba(0,0,0,0.3);
  -webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);
  border:1px solid rgba(255,255,255,0.15);
  border-radius:9999px;padding:4px 16px;
  font-size:16px;font-weight:600;
}
.pub-card-body{
  padding:24px 24px 24px;display:flex;flex-direction:column;flex:1;
}
.pub-card-body h4{
  font-size:20px;font-weight:700;line-height:1.5;color:#1a1035;
  flex:1;
}
.pub-card-footer{
  display:flex;justify-content:space-between;align-items:flex-end;
  padding:24px 24px 24px;
}
.pub-card-meta{display:flex;flex-direction:column;gap:4px}
.pub-card-meta-row{display:flex;gap:12px;font-size:16px;color:#5a5278}
.pub-card-meta-label{font-weight:600;color:#1a1035;min-width:72px}
.pub-card-arrow{
  width:40px;height:40px;border-radius:20px;
  border:2px solid #1a1035;
  display:flex;align-items:center;justify-content:center;
  flex-shrink:0;
  transition:all .3s;
}
.pub-card-arrow img{width:48px;height:auto;transition:transform .3s;}
/* .pub-card:hover .pub-card-arrow img { transform: translateX(4px); } */

.cta-section{
  background:#14102a;padding:96px 0;
  position:relative;
}
.cta-box{
  background:rgba(30,14,62,0.6);
  -webkit-backdrop-filter:blur(24px);backdrop-filter:blur(24px);
  border:1px solid rgba(140,100,210,0.2);border-radius:20px;
  padding:48px 32px;
  position:relative;overflow:hidden;text-align:center;
}
.cta-bg{
  position:absolute;inset:0;pointer-events:none;
  background:
    radial-gradient(ellipse 60% 50% at 50% 30%,rgba(104,54,168,0.3) 0%,transparent 70%),
    linear-gradient(135deg,rgba(30,14,62,0.5) 0%,rgba(58,21,104,0.3) 50%,rgba(30,14,62,0.5) 100%);
}
.cta-inner{position:relative;z-index:1}
.cta-box h2{
  font-size:48px;font-weight:800;
  line-height:1.2;margin:24px 0 16px;color:#fff;
}
@media (max-width: 768px) {
  .cta-box h2 { font-size: 32px !important; margin: 16px 0 12px !important; }
}
.cta-box p{font-size:16px;color:#fff;max-width:520px;margin:0 auto 40px;line-height:1.6}
.cta-contact{font-size:12px;color:#9e95b8;margin-top:16px}

/* Modal close overlay - mobile only, bypasses HubSpot iframe touch capture */
.modal-close-overlay {
  display: none; /* hidden on desktop, desktop uses in-card button */
  position: fixed;
  top: 20px;
  right: 2.5vw;
  z-index: 3000;
  cursor: pointer;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  transition: background 0.2s;
}
.modal-close-overlay:hover { background: #f0f0f0; }
@media (max-width: 768px) {
  .modal-close-overlay { display: flex; }
  .modal-close-desktop { display: none; }
}

.btn-gradient-float {

  background: linear-gradient(135deg, #6836a8 0%, #be35b0 100%);
  color: #fff;
  border: none;
  border-radius: 9999px;
  padding: 16px 36px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 8px 40px rgba(104, 54, 168, 0.4);
  transition: transform 0.25s, box-shadow 0.25s, filter 0.25s;
  text-decoration: none;
}
.btn-gradient-float:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 48px rgba(104, 54, 168, 0.55);
  filter: brightness(1.08);
}

/* HubSpot Form Modal Styles */
.request_modal {
  position: relative;
  width: 100%;
  min-height: 400px;
  background: #fff;
}

@media (min-width: 769px) {
  .request_modal {
    min-height: 794px;
  }
}

.form-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  z-index: 10;
}

.hs-form-frame {
  width: 100%;
  min-height: 400px;
}

@media (max-width: 768px) {
  .hs-form-frame {
    min-height: 600px; /* Give it more room on mobile initially */
  }
}

/* ══════ Workspace Demo Video Section (Full Screen & Snapping) ══════ */
.home_wrap {
  scroll-snap-type: y proximity;
  -webkit-overflow-scrolling: touch;
}

.banner_box {
  scroll-snap-align: start;
  scroll-snap-stop: normal;
}

.workspace-demo-section {
  position: relative;
  z-index: 10;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  box-sizing: border-box;
  background-color: #FAF8FE !important;
  border-top: 1px solid #EAE3F2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 136px; /* 避开固定 Header 与公告栏(约132px) */
  padding-bottom: 24px;
  overflow: hidden;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
}

.workspace-demo-container {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  width: 100%;
  max-width: 1280px; /* 适度收敛容器宽度，视觉更精致贴合 */
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.workspace-demo-card {
  /* 严格保持 16:9 画幅，尺寸适度收敛，完全贴合视频，四周不留黑边 */
  aspect-ratio: 16 / 9;
  width: 100%;
  max-height: calc(100vh - 180px);
  max-width: min(1180px, calc((100vh - 180px) * 16 / 9));
  height: auto;
  border-radius: 16px;
  overflow: hidden;
  background: #120a20;
  box-shadow: 0 0 0 1px rgba(104, 54, 168, 0.22), 0 20px 60px -12px rgba(104, 54, 168, 0.22), 0 10px 30px -4px rgba(0, 0, 0, 0.25);
  margin: auto;
  transition: box-shadow 0.3s ease;
}

.workspace-demo-card:hover {
  box-shadow: 0 0 0 1px rgba(104, 54, 168, 0.35), 0 24px 70px -12px rgba(104, 54, 168, 0.3), 0 12px 36px -4px rgba(0, 0, 0, 0.3);
}

.workspace-demo-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

/* 贯穿分割线：贴在两板块真实物理交界上 */
.workspace-demo-divider {
  width: 100%;
  height: 1px;
  background: #E6DCF0;
  position: relative;
  z-index: 10;
  flex-shrink: 0;
}

@media (max-width: 992px) {
  .workspace-demo-section {
    padding-top: 136px;
    padding-bottom: 20px;
  }
  .workspace-demo-container {
    padding: 0 20px;
  }
}

@media (max-width: 768px) {
  .home_wrap {
    scroll-snap-type: none !important;
  }
  .banner_box {
    scroll-snap-align: none !important;
  }
  .workspace-demo-section {
    height: auto !important;
    min-height: unset !important;
    scroll-snap-align: none !important;
    padding: 36px 0 36px !important; /* 移动端自然紧凑间距 */
  }
  .workspace-demo-container {
    height: auto !important;
    flex: none !important;
    padding: 0 16px !important;
  }
  .workspace-demo-card {
    border-radius: 12px !important;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    max-height: none !important;
    aspect-ratio: 16 / 9 !important;
  }
}

@media (max-width: 480px) {
  .workspace-demo-section {
    padding: 28px 0 28px !important;
  }
  .workspace-demo-container {
    padding: 0 12px !important;
  }
}
</style>
